/**
 * @file JS - Class - VectorLayer.js et VectorLayerStyle
 * @author Adam Lemire
 * @since 2019/06/19
 * @version 1.0
 * @summary Dernières modifications : 2023/03/02
 */

import fetch from 'isomorphic-fetch';
import MVT from 'ol/format/MVT.js';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { Cluster, VectorTile as VectorTileSource, Vector as VectorSource } from 'ol/source';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import GeoJSON from 'ol/format/GeoJSON';
import { Vector as OlVectorLayer, VectorImage as OlVectorImageLayer } from 'ol/layer';
import Feature from 'ol/Feature'; // eslint-disable-line no-unused-vars
import { get as getProjection } from 'ol/proj.js';
import { all as strategyAll } from 'ol/loadingstrategy';
import WMTSCapabilities from 'ol/format/WMTSCapabilities.js';
import WMSCapabilities from 'ol/format/WMSCapabilities';
import Layer from './Layer.js';
import VectorLayerStyle from './VectorLayerStyle.js';
import { ClassificationType, TypeAttribut, StyleTypes, EPSG, FeatureType } from '../../helpers/Constants.js'; // eslint-disable-line no-unused-vars
import { gisSettings } from '../../gisSettings.js';
import { Evenements } from '../../events/Evenements.js';

/*
 @todo bugs z-index des layers si change dans liste a gauche
 @todo bug sélecteur - Payload too large   si selecte tout le qc
*/

const OPTION_LIBELLÉ = gisSettings.options.layer.coucheLabel.active;
const GEOSERVER_URL = gisSettings.geoserver.url.serveurCieq;
const API_URL_IMPORTER_FICHIER_DONNÉES = gisSettings.url.api.importerFichierDonneesEspaceChercheur;

const DEFAUT_NOM_COUCHE = 'sous-districts 1911';
const DEFAUT_WORKSPACE = 'ibalsac';
const DEFAUT_SOURCENAME = 'qc_sdr_1911';

const SUFFIXE_NOM_COUCHE_LIBELLÉ = '_pts';
const SUFFIXE_NOM_COUCHE_CONVERTIE_POINTS = '_pts';
const URL_GEOSERVER_WFS_SUFFIXE = '/ibalsac';
const PROJECTION_PROJET = EPSG[3857];

const SourceLibellé = {
  GEOSERVER: 'geoserver',
  DONNÉES_IMPORTÉES: 'données'
};

/**
 * Classe VectorLayer représente une couche vectorielle de type point extends VectorLayer
 *
 * @example
 * const vectorLayer = new VectorLayer({
    name: name,
    url: url,
    layerWMSName: layerWMSName
    });
 */
export default class VectorLayer extends Layer {
  /**
   * @param { object } options
   * @param { number } options.id
   * @param { string } options.name
   * @param { string } options.urlGeoserver
   * @param { string } options.sourceWorkspace
   * @param { string } options.sourceName
   * @param { object } options.importedData - (optionnel) données importées à joindre à la layer
   * @param { object } options.jsonObject - (optionnel) cet objet créé à partir de toJSON()
   * @param { object } options.jsonDonneesImportees - (optionnel) cet objet créé dans SingleMapPage.fromJSON
   */
  constructor ({
    id = 0,
    name = DEFAUT_NOM_COUCHE,
    urlGeoserver = GEOSERVER_URL,
    sourceWorkspace = DEFAUT_WORKSPACE,
    sourceName = DEFAUT_SOURCENAME,
    importedData = false,
    jsonObject = false,
    jsonDonneesImportees = false
  }) {
    if (jsonObject) {
      super(jsonObject.id, jsonObject.name, jsonObject.visible);
      this._importedData = jsonDonneesImportees;
      this._créerDepuisJSON(jsonObject);
      this._olLayer = this._créerVectorTileLayerDepuisJSON();
      this._olLayerLibellés = OPTION_LIBELLÉ ? this._créerCoucheLibellés() : false;
      this._parametresLibellé = {
        source: SourceLibellé.GEOSERVER,
        nomColonne: 'nom_sdr'
      };
    } else {
      super(id, name);
      /** @type { string } */
      this._sourceWorkspace = sourceWorkspace;
      /** @type { string } */
      this._sourceName = sourceName;
      /** @type { string } */
      this._urlGeoserver = urlGeoserver;
      /** @type { string } */
      this._sourceUrl = urlGeoserver + '/gwc/service/wmts?' +
        'REQUEST=GetTile&' +
        'SERVICE=WMTS&' +
        'VERSION=1.0.0&' +
        'LAYER=' + sourceWorkspace + ':' + sourceName + '&' +
        'TILEMATRIX=' + PROJECTION_PROJET + ':{z}&' +
        'TILEMATRIXSET=' + PROJECTION_PROJET + '&' +
        'FORMAT=application/vnd.mapbox-vector-tile&' +
        'TILECOL={x}&' +
        'TILEROW={y}';
      /** @type { FeatureType } */
      this._featureType = null;
      /** @type { string[] } */
      this._olLayerColumns = [];
      /** @type { object }  */
      this._attributesTypes = {};
      /** @type { VectorLayerStyle } */
      this._vectorLayerStyle = new VectorLayerStyle({});
      /** @type { number } */
      this._zIndex = 0;
      this._importedData = importedData;
      this._urlDonneesImportees = '';
      this._idSelectedFeatures = [];
      /** @type { WMTSCapabilities }  */
      this._capabilities = undefined;
      /** @type { VectorTileLayer } */
      this._olLayer = this._créerVectorTileLayer();
      this._libellésExistent = OPTION_LIBELLÉ ? this._vérifierSiLibellésExistent() : false; // undefined : false;
      this._olLayerLibellés = OPTION_LIBELLÉ ? this._créerCoucheLibellés() : false;
      this._layerLibellésVisible = false;
      this._parametresLibellé = {
        source: SourceLibellé.GEOSERVER,
        nomColonne: 'nom_sdr'
      };
      this._convertiEnPointPossible = true; // ! créer fn pour vérifier
      this._convertiEnPoint = false;
      console.log('layer: ' + this.name + ' - zindex: ' + this.zIndex);
    }
  }

  get olLayer () { return this._olLayer; }

  set olLayer (olLayer) { this._olLayer = olLayer; }

  get vectorLayerStyle () { return this._vectorLayerStyle; }

  get styleOptions () { return this._vectorLayerStyle; }

  set styleOptions (styleOptions) { this._vectorLayerStyle = styleOptions; }

  get sourceName () { return this._sourceWorkspace + '.' + this._sourceName; }

  get zIndex () { return this._zIndex; }

  set zIndex (zIndex) {
    this._zIndex = zIndex;
    console.log('layer: ' + this.name + ' - zindex: ' + this.zIndex);
  }

  get features () { return this._importedData.features; }

  get olLayerColumns () { return this._olLayerColumns; }

  get attributesTypes () { return this._attributesTypes; }

  get featureType () { return this._featureType; }

  set featureType (featureType) {
    this._featureType = obtenirFeatureTypeNettoyé(featureType);
    this._notifierFeatureTypePret(this._featureType);
  }

  get joinField () {
    if (this._importedData && this._importedData.join_field) return this._importedData.join_field;
    else return undefined;
  }

  get donneesImportees () { return this._importedData; }

  /** @returns { boolean } */
  get inclureDonneesNonImportees () { return this._importedData.inclureDonneesNonImportees || false; }

  /** @param { boolean } valeur */
  set inclureDonneesNonImportees (valeur) {
    if (this._importedData) {
      this._importedData.inclureDonneesNonImportees = valeur;
      this._vectorLayerStyle.styleAutres.visible = valeur;
    }
  }

  get idSelectedFeatures () { return this._idSelectedFeatures; }

  set idSelectedFeatures (idSelectedFeatures) { this._idSelectedFeatures = idSelectedFeatures; }

  get urlDonneesImportees () { return this._urlDonneesImportees; }

  set urlDonneesImportees (urlDonneesImportees) { this._urlDonneesImportees = urlDonneesImportees; }

  get olLayerLibellés () { return this._olLayerLibellés; }

  get layerLibellésVisible () { return this._layerLibellésVisible; }

  set layerLibellésVisible (valeur) { this._layerLibellésVisible = valeur; }

  /** @returns { boolean } */
  async getLibellésExistent () {
    if (OPTION_LIBELLÉ) {
      const libellésExistent = await this._libellésExistent;
      if (await libellésExistent === undefined) {
        const featureType = obtenirFeatureTypeNettoyé(await this._obtenirFeatureTypeDepuisSourceCapabilities());
        const suffixeNomCoucheLibellé = await featureType === FeatureType.POINT ? '' : SUFFIXE_NOM_COUCHE_LIBELLÉ;
        const urlCapabilitiesLayerLibellés = this._obtenirUrlCapabilitiesLayerLibellés(await suffixeNomCoucheLibellé);
        this._libellésExistent = await VectorLayer.vérifierSiCoucheLibellésSurGeoserver(urlCapabilitiesLayerLibellés);
      }

      const existe = await this._libellésExistent;
      return !!existe;
    } else {
      return false;
    }
  }

  get convertiEnPoint () { return this._convertiEnPoint; }

  set convertiEnPoint (valeur) {
    if (this._convertiEnPointPossible) {
      valeur ? this._convertirSourceEnPoints() : this._restaurerSourceOriginale();
      this._convertiEnPoint = valeur;
    } else {
      this._convertiEnPoint = false;
    }
  }

  /*
   * PUBLIC METHODS
   */

  async updateStyle () {
    const _this = this;
    const olLayer = await this.olLayer;
    if (this._importedData) {
      this.actualiserStyleDonnéesImportées();
    } else {
      olLayer.setStyle(_this._setStyle.bind(_this));
    }

    this._notifyLayerStyleChangedListener(this._vectorLayerStyle);
  }

  /**
   * @description Sélection ne provenant pas d'un clic sur la carte
   * @param { number [] } idSelectedFeatures
   */
  async updateStyleFromSelectedFeatures (idSelectedFeatures) {
    const _this = this;
    const olLayer = await this.olLayer;

    this._idSelectedFeatures = idSelectedFeatures;
    olLayer.setStyle(_this._setStyle.bind(_this));
  }

  /** @description Sélection provenant d'un clic sur la carte - une seule feature est sélectionnée */
  async updateStyleFromSelectionChanged () {
    const _this = this;
    const olLayer = await this.olLayer;
    const idSelectedFeatures = async function () {
      const idSelectedFeatures = await _this._idSelectedFeatures;
      return idSelectedFeatures;
    };
    if (this._importedData) {
      idSelectedFeatures().then(_this.actualiserStyleDonnéesImportées());
    } else {
      idSelectedFeatures().then(olLayer.setStyle(_this._setStyle.bind(_this)));
    }
  }

  async actualiserStyleDonnéesImportées () {
    const _this = this;
    const olLayer = await this.olLayer;
    const donnéesImportées = await _this._importedData;

    const attenteDonnéesImportées = async function () {
      const donnéesImportées_ = await donnéesImportées;
      return donnéesImportées_;
    };

    attenteDonnéesImportées().then(olLayer.setStyle(_this._assignerStyleAvecDonnéesImportées.bind(_this, donnéesImportées)));
  }

  async actualiserStyleDonnéesImportéesJSON () {
    const _this = this;
    const olLayer = await this.olLayer;
    const donnéesImportées = await _this._importedData;

    const attenteDonnéesImportées = async function () {
      const donnéesImportées_ = await donnéesImportées;
      return donnéesImportées_;
    };

    attenteDonnéesImportées().then(async function () {
      if (donnéesImportées) {
        const valeursAttributs = VectorLayer.obtenirValeursAttributsDeDonnéesImportées(_this._vectorLayerStyle.groupValueColumn, donnéesImportées);
        const classification = async function () {
          const classification_ = await _this._vectorLayerStyle.creerClassificationDepuisJSON(valeursAttributs);
          return classification_;
        };
        classification().then(olLayer.setStyle(_this._assignerStyleAvecDonnéesImportées.bind(_this, donnéesImportées)));
      } else {
        olLayer.setStyle(_this._assignerStyleAvecDonnéesImportées.bind(_this, donnéesImportées));
      }
    });
  }

  /*
   * PRIVATE METHODS
   */

  async _obtenirFeatureTypeDepuisSourceCapabilities () {
    try {
      const capabilities = await this._getSourceCapabilities();
      return capabilities.Contents.Layer[0].Style[0].Identifier;
    } catch (e) {
      console.log('erreur _getSourceCapabilities');
    }
  }

  /**
   * @param { string } suffixeNomCoucheLibellé
   * @returns { string }
   */
  _obtenirUrlCapabilitiesLayerLibellés (suffixeNomCoucheLibellé) {
    return this._urlGeoserver + '/' + this._sourceWorkspace + '/' + this._sourceName + suffixeNomCoucheLibellé + '/gwc/service/wms?REQUEST=GetCapabilities';
  }

  /**
   * @description fonction créant l'url pour aller chercher les vectorTiles sur Geoserver. Usage: tileUrlFunction: _this._tileUrlFunction.bind(_this)
   * @param { number[] } tileCoord
   * @returns { UrlFunction }
   */
  _tileUrlFunction (tileCoord) {
    const _this = this;
    return (
      _this._urlGeoserver + '/gwc/service/wmts?' +
        'REQUEST=GetTile&' +
        'SERVICE=WMTS&' +
        'VERSION=1.0.0&' +
        'LAYER=' + _this._sourceWorkspace + ':' + _this._sourceName + '&' +
        'TILEMATRIX=' + PROJECTION_PROJET + ':{z}&' +
        'TILEMATRIXSET=' + PROJECTION_PROJET + '&' +
        'FORMAT=application/vnd.mapbox-vector-tile&' +
        'TILECOL={x}&' +
        'TILEROW={y}'
    )
      .replace('{z}', String(tileCoord[0] * 2))
      .replace('{x}', String(tileCoord[1]))
      .replace('{y}', String(tileCoord[2]));
  }

  /**
   * @param { Feature } feature
   * @returns { Style }
   */
  _setStyle (feature) {
    const _this = this;

    /* cluster de points
    const size = feature.get('features') ? feature.get('features').length : 0;

    if (size > 0) {
      const style = this._vectorLayerStyle.singleStyle;
      return this._obtenirStyle(style, feature);
    }
    */

    const featureExclue = this._verifierSiDoitExclureFeature(feature);

    if (!this._vectorLayerStyle.styleAutres.visible && featureExclue) {
      return;
    }

    if (!this._featureType && !featureExclue) {
      this.featureType = feature.getGeometry().getType();
      this.creerListeAttributs();
      console.log('!!!!! ce bloc ne devrais plus etre exécuté !!!!!');
    }

    if ((!this._importedData && this._idSelectedFeatures && this._idSelectedFeatures[0] === feature.getId()) || (this.joinField && this._idSelectedFeatures && this._idSelectedFeatures.includes(feature.get(_this.joinField)))) {
      return VectorLayerStyle.obtenirStyleSelectionSelonType(this._featureType, this._vectorLayerStyle.singleStyle);
    } else if (this._vectorLayerStyle.styleAutres.visible && featureExclue) {
      return this._obtenirStyle(this._vectorLayerStyle.styleAutres, feature);
    } else {
      const col = this._vectorLayerStyle.groupValueColumn;
      const style = this._vectorLayerStyle.styleType === StyleTypes.SINGLE
        ? this._vectorLayerStyle.singleStyle
        : (this._importedData
            ? this._getImportedDataStyle(feature)
            : this._vectorLayerStyle.getSymbolStyle(feature.get(col))
          );
      if (!style) return;
      return this._obtenirStyle(style, feature);
    }
  }

  /**
   * @param { Feature } olFeature
   * @returns { boolean }
   * */
  _verifierSiDoitExclureFeature (olFeature) {
    const donnéesImportées = this._importedData;
    if (donnéesImportées) {
      const joinField = donnéesImportées.join_field;
      return !donnéesImportées.features.find(obj => obj[joinField] === olFeature.getProperties()[joinField]);
    } else {
      return false;
    }
  }

  /**
   * @param { object } donnéesImportées
   * @param { Feature } feature
   * @returns { Style }
   */
  _assignerStyleAvecDonnéesImportées (donnéesImportées, feature) {
    const _this = this;
    const featureExclue = this._verifierSiDoitExclureFeatureSelonDonnées(feature, donnéesImportées);
    let _donnéesImportées = donnéesImportées;

    if (!this._vectorLayerStyle.styleAutres.visible && featureExclue) {
      return;
    }

    if (donnéesImportées && !this._verificationDonnéesNullesFaite && !featureExclue) {
      _donnéesImportées = VectorLayer.vérifierDonnéesImportées(donnéesImportées);
      this._verificationDonnéesNullesFaite = true;
    }

    if ((!_donnéesImportées && this._idSelectedFeatures && this._idSelectedFeatures[0] === feature.getId()) || (this.joinField && this._idSelectedFeatures && this._idSelectedFeatures.includes(feature.get(_this.joinField)))) {
      return VectorLayerStyle.obtenirStyleSelectionSelonType(this._featureType, this._vectorLayerStyle.singleStyle);
    } else if (this._vectorLayerStyle.styleAutres.visible && featureExclue) {
      return this._obtenirStyle(this._vectorLayerStyle.styleAutres, feature);
    } else {
      const col = this._vectorLayerStyle.groupValueColumn;
      const style = this._vectorLayerStyle.styleType === StyleTypes.SINGLE
        ? this._vectorLayerStyle.singleStyle
        : (this._importedData
            ? this._getImportedDataStyle(feature)
            : this._vectorLayerStyle.getSymbolStyle(feature.get(col))
          );
      if (!style) return;
      return this._obtenirStyle(style, feature);
    }
  }

  /**
   * @param { Feature } olFeature
   * @param { object } donnéesImportées
   * @returns { boolean }
   */
  _verifierSiDoitExclureFeatureSelonDonnées (olFeature, donnéesImportées) {
    if (donnéesImportées) {
      const joinField = donnéesImportées.join_field;
      return !donnéesImportées.features.find(obj => obj[joinField] === olFeature.getProperties()[joinField]);
    } else {
      return false;
    }
  }

  /**
   * @param { Feature } feature
   * @returns { object } (style de la feature)
   */
  _getImportedDataStyle (feature) {
    /**
     * @description nom de la colonne permettant la liaison entre la couche spatiale (geoserver) et des données (fichier json reçu)
     * @type { string } */
    const joinField = this._importedData.join_field;
    const vectorLayerStyle = this._vectorLayerStyle;
    const col = vectorLayerStyle.groupValueColumn;
    const obj = this._importedData.features.find(obj => obj[joinField] === feature.getProperties()[joinField]);

    return this._vectorLayerStyle.getSymbolStyle(obj[col]);
  }

  creerListeAttributs () {
    const _this = this;
    if (this._importedData) {
      const dataFeature = this._importedData.features[0];
      for (const property in dataFeature) {
        _this._olLayerColumns.push(property);
        _this._attributesTypes[property] = _this._verifierTypeAttribut(property);
      }
    } else {
      console.log('ne contient pas de données importées');
    }
    this._notifierChoixAttributChange(this._olLayerColumns);
  }

  /**
   * @param { object } donnéesImportées
   * @returns { object }
   */
  static vérifierDonnéesImportées (donnéesImportées) {
    const dataFeature = donnéesImportées.features[0];
    for (const attribut in dataFeature) {
      const listeValeurs = donnéesImportées.features.map((feature) => feature[attribut]);

      if (verifierSiListeContientValeursString(listeValeurs)) {
        // console.log('string');
      } else if (verifierSiListeContientEntierOuNulSeulement(listeValeurs)) {
        donnéesImportées.features.forEach(function (feature) {
          feature[attribut] = feature[attribut] ? parseInt(feature[attribut]) : 0;
        });
      } else {
        donnéesImportées.features.forEach(function (feature) {
          feature[attribut] = feature[attribut] ? parseFloat(feature[attribut]) : 0;
        });
      }
    }
    return donnéesImportées;
  }

  /**
   * @param { string } nomColonne
   * @returns { string } TypeAttribut
   */
  _verifierTypeAttribut (nomColonne) {
    let typeAttribut;
    const donnéesImportées = this._importedData;
    const listeValeurs = donnéesImportées.features.map((feature) => feature[nomColonne]);

    if (verifierSiListeContientValeursString(listeValeurs)) {
      typeAttribut = TypeAttribut.STRING;
    } else if (verifierSiListeContientEntierOuNulSeulement(listeValeurs)) {
      typeAttribut = TypeAttribut.INTEGER;
      donnéesImportées.features.forEach(function (feature) {
        feature[nomColonne] = feature[nomColonne] ? parseInt(feature[nomColonne]) : 0;
      });
    } else {
      typeAttribut = TypeAttribut.FLOAT;
      donnéesImportées.features.forEach(function (feature) {
        feature[nomColonne] = feature[nomColonne] ? parseFloat(feature[nomColonne]) : 0;
      });
    }

    // console.log(nomColonne + ': ' + typeAttribut);
    return typeAttribut;
  }

  /**
   * @param { string } column
   * @returns { number[] | string[] }
   */
  getLayerColumnData (column) {
    const columnData = [];
    if (this._importedData) {
      this._importedData.features.forEach(function (feature) {
        columnData.push(feature[column]);
      });
      columnData.sort(function (a, b) { return a - b; }); // retourne toutes les valeurs (nécessaire pour statistiques, mais pas pour autres cas)
    }
    return columnData;
  }

  /**
   * @param { string } attribut - nom colonne
   * @param { object } donnéesImportées
   * @returns { number[] | string[] }
   */
  static obtenirValeursAttributsDeDonnéesImportées (attribut, donnéesImportées) {
    const valeursAttributs = [];
    if (donnéesImportées) {
      donnéesImportées.features.forEach(function (feature) {
        valeursAttributs.push(feature[attribut]);
      });
      valeursAttributs.sort(function (a, b) { return a - b; }); // retourne toutes les valeurs (nécessaire pour statistiques, mais pas pour autres cas)
    }
    return valeursAttributs;
  }

  /**
   * @param { string } nomColonne
   * @returns { string [] | number [] }
   */
  obtenirValeursDistinctes (nomColonne) {
    const valeursDistinctes = [];
    if (this._importedData) {
      this._importedData.features.forEach(function (feature) {
        if (!valeursDistinctes.includes(feature[nomColonne])) {
          valeursDistinctes.push(feature[nomColonne]);
        }
      });
      valeursDistinctes.sort(function (a, b) { return a - b; });
    } else { console.log('Données non-importée'); }

    return valeursDistinctes;
  }

  /*
   * STYLES
   */

  /**
   * @param { object } style - voir VectorLayerStyle.js
   * @param { Feature } feature
   * @returns { Style }
   */
  _obtenirStyle (style, feature) {
    switch (this._featureType) {
      case FeatureType.POLYGON:
        return VectorLayerStyle.obtenirStylePolygone(style);
      case FeatureType.LINE:
        return VectorLayerStyle.obtenirStyleLigne(style);
      case FeatureType.POINT:
        return VectorLayerStyle.obtenirStylePoint(style, feature);
      default:
        console.log('Ce cas ne devrait pas arriver');
        return VectorLayerStyle.obtenirStyleParDéfaut(style);
    }
  }

  /** @returns { VectorTileLayer } */
  async _créerVectorTileLayer () {
    const _this = this;
    const source = await this._créerSource(await this._importedData);

    if (source instanceof Cluster || source instanceof VectorSource) {
      const olVectorLayer = new OlVectorLayer({
        source: source,
        renderBuffer: 200,
        style: _this._setStyle.bind(_this)
      });
      return olVectorLayer;
    }

    const tileLayer = new VectorTileLayer({
      renderMode: 'hybrid',
      renderBuffer: 100,
      source: source,
      style: _this._setStyle.bind(_this)
    });

    return tileLayer;
  }

  /** @returns { VectorTileLayer } */
  async _créerVectorTileLayerDepuisJSON () {
    const source = await this._créerSource(await this._importedData);

    if (source instanceof Cluster || source instanceof VectorSource) {
      const olVectorLayer = new OlVectorImageLayer({
        source: source,
        renderBuffer: 200
      });
      return olVectorLayer;
    }

    const tileLayer = new VectorTileLayer({
      renderMode: 'hybrid',
      renderBuffer: 100,
      source: source
    });

    return tileLayer;
  }

  async _convertirSourceEnPoints () {
    const _this = this;
    const source = await this._obtenirPointVectorSource(SUFFIXE_NOM_COUCHE_CONVERTIE_POINTS);

    this._featureType = FeatureType.POINT;
    // console.log('source: %o', await source);
    const nouveauOlVectorLayer = new OlVectorLayer({
      source: source,
      renderBuffer: 200,
      style: _this._setStyle.bind(_this)
    });
    // this._olLayer = olVectorLayer;
    Evenements.vectorLayerSourceChangee.notify({ vectorLayer: this, nouveauOlVectorLayer: nouveauOlVectorLayer });
  }

  async _restaurerSourceOriginale () {
    this._featureType = FeatureType.POLYGON;
    const olVectorLayerOriginale = await this._créerVectorTileLayer();
    Evenements.vectorLayerSourceChangee.notify({ vectorLayer: this, nouveauOlVectorLayer: olVectorLayerOriginale });
  }

  /** @returns { VectorTileSource | VectorSource | Cluster } */
  async _créerSource () {
    try {
      const capabilities = await this._getSourceCapabilities();
      this._capabilities = capabilities;
      const featureType = obtenirFeatureTypeNettoyé(capabilities.Contents.Layer[0].Style[0].Identifier);
      this.featureType = featureType;
      // console.log(featureType);

      /* test cluster points
      if (featureType === FeatureType.POINT) {
        console.log('TEST ICI POINTS');
        const source = await this._obtenirClusterVectorSource(featureType);
        return source;
      }
      */

      if (featureType === FeatureType.POINT) {
        const source = await this._obtenirPointVectorSource();
        return source;
      }

      const source = await this._getSource(capabilities);
      return source;
    } catch (e) {
      console.log(e);
    }
  }

  // ! p-e faire un retry automatique dans le catch
  /** @returns { WMTSCapabitilies } */
  async _getSourceCapabilities () {
    try {
      const _this = this;
      const url = _this._urlGeoserver + '/' + _this._sourceWorkspace + '/' + _this._sourceName + '/gwc/service/wmts?REQUEST=GetCapabilities';
      const parser = new WMTSCapabilities();
      const resp = await fetch(url);
      const responseText = await resp.text();
      const capabilities = await parser.read(responseText);

      return capabilities;
    } catch (err) {
      console.log('_getSourceCapabilities: ' + err);
    }
  }

  /** @returns { VectorTileSource } */
  _getSource () {
    try {
      // Le bug de la grille visible sur les points est corrigé ici. Cela implique que les couches sur geoserveur doivent avoir l'emprise [-180, -90, 180, 90], et vectorTileLayer doit avoir renderMode: 'image'
      const _this = this;
      const projection = getProjection(PROJECTION_PROJET);
      const projectionExtent = projection.getExtent();
      const tileSize = 256; // même que la tilegrid 3857 sur geoserver
      const matrixLength = 8; // sur geoserver, la tilegrid 3857 a 23 niveaux //15 semble etre pas mal proche, ici moitié pair car skip un sur 2
      const maxResolution = 156543.03390625; // sur geoserver, tilegrid 3857, taille du pixel niveau 0
      const resolutions = new Array(matrixLength);
      const matrixIds = new Array(matrixLength);

      for (let i = 0; i < matrixLength; ++i) {
        resolutions[i] = maxResolution / Math.pow(2, i * 2); // resolutions[i] = maxResolution / Math.pow(2, i);
        matrixIds[i] = i;
      }

      const tileGrid = new WMTSTileGrid({
        extent: projectionExtent,
        tileSize: tileSize,
        matrixIds: matrixIds,
        resolutions: resolutions
      });

      return new VectorTileSource({
        format: new MVT(),
        tileGrid: tileGrid,
        projection: PROJECTION_PROJET,
        overlaps: false,
        tileSize: tileSize,
        tileUrlFunction: _this._tileUrlFunction.bind(_this)
      });
    } catch (err) {
      console.log(err);
    }
  }

  // cluster de points non-développé
  /* _obtenirClusterVectorSource () {
    const layerSource = this._obtenirPointVectorSource();

    return new Cluster({
      distance: 40,
      source: layerSource
    });
  } */

  /**
   * @param { string } suffixeNomCouche
   * @returns { VectorSource }
   */
  _obtenirPointVectorSource (suffixeNomCouche) {
    const _this = this;
    const suffixeNomCoucheString = suffixeNomCouche || '';
    const layerSource = new VectorSource({
      format: new GeoJSON(),
      url: _this._urlGeoserver + URL_GEOSERVER_WFS_SUFFIXE + '/wfs?service=WFS&' +
          'version=1.1.0&request=GetFeature&typename=' + _this._sourceWorkspace + ':' + _this._sourceName + suffixeNomCoucheString + '&' +
          'outputFormat=application/json&srsname=' + PROJECTION_PROJET,
      strategy: strategyAll
    });

    /*
    url: function (extent) {
        const url = _this._urlGeoserver + URL_GEOSERVER_WFS_SUFFIXE + '/wfs?service=WFS&' +
          'version=1.1.0&request=GetFeature&typename=' + _this._sourceWorkspace + ':' + _this._sourceName + suffixeNomCoucheString + '&' +
          'outputFormat=application/json&srsname=' + PROJECTION_PROJET;
        console.log('url pointVectorSource = %o', url);
        return url;
      },
    */

    return layerSource;
  }

  /*
   * LIBELLÉS
   */

  /** @returns { boolean } */
  async _vérifierSiLibellésExistent () {
    const featureType = obtenirFeatureTypeNettoyé(await this._obtenirFeatureTypeDepuisSourceCapabilities());
    const suffixeNomCoucheLibellé = await featureType === FeatureType.POINT ? '' : SUFFIXE_NOM_COUCHE_LIBELLÉ;
    const urlCapabilitiesLayerLibellés = this._obtenirUrlCapabilitiesLayerLibellés(await suffixeNomCoucheLibellé);

    return !!await VectorLayer.vérifierSiCoucheLibellésSurGeoserver(urlCapabilitiesLayerLibellés);
  }

  static async vérifierSiCoucheLibellésSurGeoserver (url) {
    try {
      const parser = new WMSCapabilities();
      // console.log('vérifierSiCoucheLibellésSurGeoserver - url= %o', url);
      const resp = await fetch(url);
      if (resp.ok) {
        const responseText = await resp.text();
        const capabilities = await parser.read(responseText);
        return !!capabilities;
      } else {
        const responseJSON = await resp.json();
        console.log(responseJSON.message || 'pas libellé');
        return false;
      }
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  // Pour couche de points, plus simple d'ajouter une nouvelle couche que de changer le style de la couche mais écrase style point si layer point a declutter: true
  async _créerCoucheLibellés () {
    const _this = this;
    const avecLibellés = await this.getLibellésExistent();

    if (avecLibellés) {
      const suffixeNomCoucheLibellé = this._featureType === FeatureType.POINT ? '' : SUFFIXE_NOM_COUCHE_LIBELLÉ;
      const labelLayerSource = this._obtenirPointVectorSource(suffixeNomCoucheLibellé);

      const labelLayer = new OlVectorLayer({
        source: labelLayerSource,
        declutter: true,
        style: _this._obtenirStyleLabel.bind(_this),
        zIndex: 999,
        visible: _this._layerLibellésVisible
      });

      return labelLayer;
    } else {
      return false;
    }
  }

  // @todo: si texte vient de données (non urgent)
  _obtenirStyleLabel (feature) {
    const featureExclue = this._verifierSiDoitExclureFeature(feature);
    const texte = this._parametresLibellé.source === SourceLibellé.GEOSERVER ? feature.getProperties()[this._parametresLibellé.nomColonne] : false;
    const styleLabel = this._vectorLayerStyle.styleLabel;

    if (featureExclue || !texte) return;

    if (this._featureType === FeatureType.POINT) {
      styleLabel.offsetY = -(parseInt(this._vectorLayerStyle.singleStyle.radius) + 6);
    }

    return VectorLayerStyle.obtenirStyleLabel(texte, styleLabel);
  }

  /*
   * Listeners
   */

  _notifierFeatureTypePret (featureType) { }

  registerFeatureTypeReadyListener (listener) {
    this._notifierFeatureTypePret = listener;
  }

  _notifierChoixAttributChange (layerColumns) { }

  // ! inutilisé
  registerLayerColumnsChangeListener (listener) {
    this._notifierChoixAttributChange = listener;
  }

  _notifyLayerStyleChangedListener (style) { }

  registerLayerStyleChangedListener (listener) {
    this._notifyLayerStyleChangedListener = listener;
  }

  /*
   * JSON SPECIFIC
   */

  toJSON () {
    console.log('toJSON _importedData : %o', this._importedData);
    return {
      id: this.id,
      name: this.name,
      visible: this.isVisible,
      sourceWorkspace: this._sourceWorkspace,
      sourceName: this._sourceName,
      urlGeoserver: this._urlGeoserver,
      sourceUrl: this._sourceUrl,
      featureType: this._featureType,
      attributesTypes: this._attributesTypes,
      styleOptions: this._vectorLayerStyle.toJSON(),
      olLayerColumns: this._olLayerColumns,
      zIndex: this._zIndex,
      importedData: this._importedData ? this._importedData.name : false,
      libellésExistent: this._libellésExistent || false,
      layerLibellésVisible: this._layerLibellésVisible,
      donneesImporteesMeta: this._importedData && this._importedData.api
        ? {
            api: this._importedData.api,
            url: this._urlDonneesImportees || API_URL_IMPORTER_FICHIER_DONNÉES + this._importedData.idFichierDonneesApi,
            idFichierDonneesApi: this._importedData.idFichierDonneesApi,
            nomFichier: this._importedData.name,
            inclureDonneesNonImportees: this._importedData.inclureDonneesNonImportees
          }
        : false
    };
  }

  _créerDepuisJSON (jsonObject) {
    // console.log('jsonObject de VectorLayer: %o', jsonObject);
    this._sourceWorkspace = jsonObject.sourceWorkspace;
    this._sourceName = jsonObject.sourceName;
    this._urlGeoserver = jsonObject.urlGeoserver;
    this._sourceUrl = jsonObject.sourceUrl;
    this._featureType = undefined; // nécessaire pour reconvertir valeur nulles en 0
    this._olLayerColumns = jsonObject.olLayerColumns;
    this._vectorLayerStyle = new VectorLayerStyle({ styleFromJSON: jsonObject.styleOptions });
    this._attributesTypes = jsonObject.attributesTypes;
    this._idSelectedFeatures = [];
    this._verificationDonnéesNullesFaite = false;
    this._libellésExistent = undefined;
    this._layerLibellésVisible = jsonObject.layerLibellésVisible;
  }

  /**
   * @param { object } objetJSON
   * @returns { object }
   */
  static obtenirDonnéesImportéesDepuisJSON (objetJSON) {
    if (objetJSON.donneesImporteesMeta && objetJSON.donneesImporteesMeta.api) {
      const donnéesImportées = obtenirDonnéesApiImportéesDepuisJSON(objetJSON.donneesImporteesMeta);
      return donnéesImportées;
    } else if (objetJSON.importedData) {
      return obtenirDonnéesLocalesImportéesDepuisJSON(objetJSON.importedData);
    }
  }
}

/**
 * @param { object } donneesImporteesMeta
 * @returns { object }
 */
async function obtenirDonnéesApiImportéesDepuisJSON (donneesImporteesMeta) {
  const dataUrl = donneesImporteesMeta.url;
  const response = await fetch(dataUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    }
  });
  const jsonData = await response.json();
  if (!jsonData.JSON_COUCHE) {
    console.log('Erreur ouverture données importées de espace chercheur');
    return false;
  }
  jsonData.JSON_COUCHE.api = true;
  jsonData.JSON_COUCHE.idFichierDonneesApi = donneesImporteesMeta.idFichierDonneesApi;
  console.log('ouverture donnée importée depuis json. idFichierDonneesApi = ' + donneesImporteesMeta.idFichierDonneesApi);
  jsonData.JSON_COUCHE.inclureDonneesNonImportees = donneesImporteesMeta.inclureDonneesNonImportees;

  const donnéesImportéesVérifiées = VectorLayer.vérifierDonnéesImportées(await jsonData.JSON_COUCHE);

  return await donnéesImportéesVérifiées;
}

/**
 * @deprecated
 * @param { object } donnéesJSON
 * @returns { object }
 */
function obtenirDonnéesLocalesImportéesDepuisJSON (donnéesJSON) {
  const dataUrl = './../../../_data/' + donnéesJSON + '.json'; // todo modifier dataUrl car ici en dur
  const donnéesLocale = $.getJSON(dataUrl, function (data) {
    // console.log('données locales : %o', data);
    return data;
  });
  return donnéesLocale;
}

/**
 * @param { Array } liste
 * @returns { boolean }
 */
function verifierSiListeContientValeursString (liste) {
  const contientString = liste.some(function (feature) {
    return feature && isNaN(feature) && feature !== '';
  });
  return contientString;
}

/**
 * @param { Array } liste
 * @returns { boolean }
 */
function verifierSiListeContientEntierOuNulSeulement (liste) {
  const contientDecimaux = liste.some(function (feature) {
    return parseInt(feature) !== parseFloat(feature) && feature !== '' && feature !== null && feature !== undefined;
  });
  const contientEntiers = !contientDecimaux;

  return contientEntiers;
}

/**
 * @param { string } featureType
 * @returns { FeatureType }
 */
function obtenirFeatureTypeNettoyé (featureType) {
  switch (featureType) {
    case 'MultiPolygon':
    case 'Polygon':
    case 'polygon':
      return FeatureType.POLYGON;
    case 'LineString':
    case 'MultiLineString':
    case 'line':
      return FeatureType.LINE;
    case 'Point':
    case 'point':
      return FeatureType.POINT;
    default:
      console.log('featureType non présent dans liste: %o', featureType);
      return featureType;
  }
}
